import React, { useEffect, useRef } from "react";

interface AudioVisualizerProps {
  stream: MediaStream | null;
  label: string;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ stream, label }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  useEffect(() => {
    if (!stream || !canvasRef.current) return;

    // Cleanup previous audio context and connections
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    // Create new audio context
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    audioContextRef.current = audioContext;

    // Create and configure analyser
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    analyserRef.current = analyser;

    // Create and connect source
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    sourceRef.current = source;

    // Handle high DPI displays
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvasCtx.scale(dpr, dpr);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      const WIDTH = canvas.width / dpr;
      const HEIGHT = canvas.height / dpr;

      animationFrameRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Clear canvas with semi-transparent background for trail effect
      const bgColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--background")
        .trim();
      canvasCtx.fillStyle = bgColor
        ? `${bgColor}33`
        : "rgba(221, 235, 227, 0.2)"; // 0.2 opacity
      canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

      const barWidth = (WIDTH / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * HEIGHT;

        // Get accent color from CSS variables for consistent branding
        const accentColor = getComputedStyle(document.documentElement)
          .getPropertyValue("--accent")
          .trim();

        // Use direct accent color for bars with varying opacity based on volume
        const opacity = 0.7 + (dataArray[i] / 255) * 0.3; // 0.7 to 1.0 opacity
        canvasCtx.fillStyle = accentColor;

        // Draw shadow for 3D effect
        canvasCtx.shadowColor = "rgba(0, 0, 0, 0.3)";
        canvasCtx.shadowBlur = 4;
        canvasCtx.shadowOffsetX = 0;
        canvasCtx.shadowOffsetY = 2;

        // Round the top of the bars
        canvasCtx.beginPath();
        canvasCtx.roundRect(x, HEIGHT - barHeight, barWidth, barHeight, 2);
        canvasCtx.fill();

        // Reset shadow for performance
        canvasCtx.shadowColor = "transparent";
        canvasCtx.shadowBlur = 0;
        canvasCtx.shadowOffsetX = 0;
        canvasCtx.shadowOffsetY = 0;

        x += barWidth + 1;
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stream]);

  return (
    <div className="bg-foreground/5 rounded-md p-4 my-2 border border-border transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md">
      <div className="text-sm font-medium text-foreground mb-3 text-left tracking-wide">
        {label}
      </div>
      <canvas
        ref={canvasRef}
        className="w-full h-[160px] bg-foreground/5 rounded-md border border-border"
      />
    </div>
  );
};

interface BallVisualizerProps {
  audioData: Float32Array | null;
  isActive: boolean;
}

export const BallVisualizer: React.FC<BallVisualizerProps> = ({
  audioData,
  isActive,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number | null>(null);

  // Smoother transitions
  const smoothedRadius = useRef<number>(60);
  const smoothingConstant = 0.92; // Higher value = smoother transition

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set actual canvas dimensions to match display size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Function to draw the visualizer
    const draw = () => {
      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!isActive || !audioData) {
        // When inactive, draw a small static circle
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, 60, 0, 2 * Math.PI);

        // Use accent color with low opacity
        const accentColor = getComputedStyle(document.documentElement)
          .getPropertyValue("--accent")
          .trim();
        ctx.fillStyle = `${accentColor}4D`; // 30% opacity

        // Add shadow for 3D effect
        ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;

        ctx.fill();

        // Reset shadow
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        animationFrameId.current = requestAnimationFrame(draw);
        return;
      }

      // Calculate the average amplitude from audio data
      let sum = 0;
      const dataLength = audioData.length;

      for (let i = 0; i < dataLength; i++) {
        sum += Math.abs(audioData[i]);
      }

      const average = sum / dataLength;

      // Calculate radius based on audio amplitude
      // Mapping average amplitude (usually between 0 and 1) to radius values
      const minRadius = 80;
      const maxRadius = 140;
      const targetRadius = minRadius + average * (maxRadius - minRadius);

      // Apply smoothing
      smoothedRadius.current =
        smoothedRadius.current * smoothingConstant +
        targetRadius * (1 - smoothingConstant);

      // Add shadow for 3D effect
      ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
      ctx.shadowBlur = 15;
      ctx.shadowOffsetX = 5;
      ctx.shadowOffsetY = 5;

      // Draw the circle
      ctx.beginPath();
      ctx.arc(
        canvas.width / 2,
        canvas.height / 2,
        smoothedRadius.current,
        0,
        2 * Math.PI
      );

      // Opacity based on audio level
      const opacity = 0.3 + average * 0.6; // Ranges from 0.3 to 0.9

      // Use accent color with dynamic opacity
      const accentColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim();
      const opacityHex = Math.round(opacity * 255)
        .toString(16)
        .padStart(2, "0");
      ctx.fillStyle = `${accentColor}${opacityHex}`;
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      animationFrameId.current = requestAnimationFrame(draw);
    };

    // Start animation
    draw();

    // Cleanup function
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [audioData, isActive]);

  return (
    <div className="ballVisualizer">
      <canvas ref={canvasRef} className="canvas min-h-[180px]" />
    </div>
  );
};

interface VoiceVisualizerProps {
  audioStream: MediaStream | null;
  isActive: boolean;
}

export const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({
  audioStream,
  isActive,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const smoothedAmplitude = useRef<number>(0);
  const smoothingFactor = 0.85;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Function to set the correct canvas dimensions
    const resizeCanvas = () => {
      if (!canvas) return;
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };

    // Set initial size and add resize listener
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Set up audio analyzer if stream is available
    if (audioStream) {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(audioStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyserRef.current = analyser;
    }

    // The main drawing function
    const draw = () => {
      if (!ctx || !canvas) return;

      // Make sure canvas is sized correctly
      resizeCanvas();

      // Clear the canvas completely
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Important: Completely disable all shadow effects first thing
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Calculate dimensions
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Calculate scaled sizes
      const minDimension = Math.min(canvas.width, canvas.height);
      const baseRadius = minDimension * 0.25;
      const glowRadius = baseRadius * 1.6;

      // Get our theme color
      const accentColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim();

      // Default to a light variant of accent color if not set
      const defaultAccentColor = "#2DD4BF";
      const finalAccentColor = accentColor || defaultAccentColor;

      // Calculate animation values
      let animatedRadius = baseRadius;
      let animatedOpacity = 1.0;

      // If we have audio data and are active, animate based on audio
      if (analyserRef.current && audioStream && isActive) {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average of mid-range frequencies (voice range)
        let midrangeSum = 0;
        let midrangeCount = 0;
        const midStart = Math.floor(bufferLength * 0.1);
        const midEnd = Math.floor(bufferLength * 0.6);

        for (let i = midStart; i < midEnd; i++) {
          midrangeSum += dataArray[i];
          midrangeCount++;
        }

        const amplitude =
          midrangeCount > 0 ? midrangeSum / midrangeCount / 255 : 0;

        // Apply smoothing
        smoothedAmplitude.current =
          smoothedAmplitude.current * smoothingFactor +
          amplitude * (1 - smoothingFactor);

        // Calculate new radius based on amplitude
        animatedRadius =
          baseRadius + smoothedAmplitude.current * baseRadius * 0.6;

        // Full opacity when active with audio
        animatedOpacity = 1.0;
      } else {
        // No audio stream or not connected - show static visualization
        // Pulse animation for static state
        const time = (Date.now() % 3000) / 3000;
        const pulseAmount = Math.sin(time * Math.PI * 2) * 0.05;

        // Slightly smaller radius for static state
        animatedRadius = baseRadius * (0.9 + pulseAmount);

        // Reduced opacity for static state
        animatedOpacity = 1.0;
      }

      // Draw the glow effect
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        baseRadius * 0.8,
        centerX,
        centerY,
        glowRadius
      );

      // Adjust glow opacity based on state
      const glowOpacity = audioStream && isActive ? "50" : "30";
      gradient.addColorStop(0, `${finalAccentColor}${glowOpacity}`);
      gradient.addColorStop(0.7, `${finalAccentColor}15`);
      gradient.addColorStop(1, `${finalAccentColor}00`);

      // Make sure no shadow is applied when drawing
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Draw the glow
      ctx.beginPath();
      ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw the main circle (again ensuring no shadow is applied)
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      ctx.beginPath();
      ctx.arc(centerX, centerY, animatedRadius, 0, Math.PI * 2);

      // Set fill style with proper opacity
      ctx.fillStyle = finalAccentColor;
      ctx.globalAlpha = animatedOpacity;
      ctx.fill();

      // Reset global alpha
      ctx.globalAlpha = 1.0;
    };

    // Animation loop
    const animate = () => {
      draw();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Always animate, regardless of isActive state
    animate();

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioStream, isActive]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

export default AudioVisualizer;
